import { TILE } from './constants';
import { isFree, tilePassable } from './obstacles';
import { tileAt, tileCenter } from './tilemap';
import type { State, Vec2 } from './types';
import { normalize, sub } from './vec';

/**
 * Breadth-first distances over passable tiles (no wall, no pillar in the way) from the tile
 * containing `target`. Crates count as passable: the bot shoves them aside like a player would.
 * A few hundred cells, so cheap enough per tick.
 */
export function distanceField(s: State, target: Vec2): Int32Array {
  const { map, pillars } = s;
  const field = new Int32Array(map.width * map.height).fill(-1);
  const t = tileAt(target);
  if (!tilePassable(map, pillars, t.x, t.y)) return field;
  const queue: number[] = [t.y * map.width + t.x];
  field[queue[0]] = 0;
  for (let head = 0; head < queue.length; head++) {
    const i = queue[head];
    const x = i % map.width;
    const y = (i - x) / map.width;
    const d = field[i] + 1;
    const neighbours = [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ];
    for (const [nx, ny] of neighbours) {
      if (!tilePassable(map, pillars, nx, ny)) continue;
      const j = ny * map.width + nx;
      if (field[j] !== -1) continue;
      field[j] = d;
      queue.push(j);
    }
  }
  return field;
}

/** Unit direction to walk from `from` towards `target` along the tile grid; straight if adjacent. */
export function pathDirection(s: State, from: Vec2, target: Vec2): Vec2 {
  const { map, pillars } = s;
  const here = tileAt(from);
  const there = tileAt(target);
  if (here.x === there.x && here.y === there.y) return normalize(sub(target, from));
  const field = distanceField(s, target);
  const own = field[here.y * map.width + here.x];
  if (own < 0) return normalize(sub(target, from));
  let best: Vec2 | null = null;
  let bestD = own;
  const neighbours = [
    [here.x + 1, here.y],
    [here.x - 1, here.y],
    [here.x, here.y + 1],
    [here.x, here.y - 1],
  ];
  for (const [nx, ny] of neighbours) {
    if (!tilePassable(map, pillars, nx, ny)) continue;
    const d = field[ny * map.width + nx];
    if (d >= 0 && d < bestD) {
      bestD = d;
      best = tileCenter(nx, ny);
    }
  }
  return best ? normalize(sub(best, from)) : normalize(sub(target, from));
}

/** Keeps a desired direction from running into a wall or pillar: tries small turns if the way is blocked. */
export function steerClear(s: State, pos: Vec2, radius: number, dir: Vec2): Vec2 {
  if (dir.x === 0 && dir.y === 0) return dir;
  const look = radius + TILE * 0.5;
  const fits = (d: Vec2) => isFree(s.map, s.pillars, pos.x + d.x * look, pos.y + d.y * look, radius);
  if (fits(dir)) return dir;
  for (const deg of [45, -45, 90, -90]) {
    const rad = (deg * Math.PI) / 180;
    const c = Math.cos(rad);
    const sn = Math.sin(rad);
    const turned = { x: dir.x * c - dir.y * sn, y: dir.x * sn + dir.y * c };
    if (fits(turned)) return turned;
  }
  return dir;
}

export function perpendicular(v: Vec2, side: number): Vec2 {
  return { x: -v.y * side, y: v.x * side };
}
